import { QueryClient, useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { BevoegdGezagOutput, MilieudienstOutput } from '../types/bevoegdGezag';
import { PagedResponse, PaginationQueryParameters } from '../types/core';
import { TeeltplanIndex } from '../types/domain';
import { AggregatieRapportInput, AggregatieLandelijkRapportInput, RapportageArchiefItem, Review } from '../types/reports';
import { createFilterParams, fetchAuthenticatedForJson, fetchAuthenticatedForBlob } from '../utils';

const QUERY_KEY = ['reports'];
const BASE_URL = import.meta.env.VITE_REPORT_API_URL;

export const useGetEnvironmentReports = (params?: PaginationQueryParameters<TeeltplanIndex>): UseQueryResult<PagedResponse<TeeltplanIndex>, Error> =>
  useQuery<PagedResponse<TeeltplanIndex>, Error>({
    queryKey: [...QUERY_KEY, 'environmentReportOverview', params],
    queryFn: () => fetchAuthenticatedForJson<PagedResponse<TeeltplanIndex>>(`${BASE_URL}/rapportages/milieu?${createFilterParams(params ?? {})}`),
  });

export const useGetRapportageArchief = (
  params?: PaginationQueryParameters<RapportageArchiefItem>
): UseQueryResult<PagedResponse<RapportageArchiefItem>, Error> =>
  useQuery<PagedResponse<RapportageArchiefItem>, Error>({
    queryKey: [...QUERY_KEY, 'rapportageArchief', params],
    queryFn: () => fetchAuthenticatedForJson<PagedResponse<RapportageArchiefItem>>(`${BASE_URL}/rapportages/archief?${createFilterParams(params ?? {})}`),
  });

export const useGetRapportageArchiefRekenmotorVersies = (registratieJaar?: number, enabled: boolean = false): UseQueryResult<string[], Error> =>
  useQuery<string[], Error>({
    queryKey: [...QUERY_KEY, 'rapportageArchiefRekenmotorVersies', registratieJaar],
    queryFn: () =>
      registratieJaar
        ? fetchAuthenticatedForJson<string[]>(`${BASE_URL}/rapportages/archief/rekenmotor-versies?registratieJaar=${registratieJaar}`)
        : Promise.reject(new Error('Missing registratieJaar')),
    enabled,
  });

export const useGetEnvironmentReport = (teeltPlanId?: number, registratieJaar?: number): UseQueryResult<Blob | undefined, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'environmentReports', teeltPlanId, registratieJaar],
    retry: false,
    queryFn: () =>
      teeltPlanId && registratieJaar
        ? fetchAuthenticatedForBlob<Blob>(`${BASE_URL}/rapportages/milieu/${teeltPlanId}?registratieJaar=${registratieJaar}`, {}, 120_000)
        : Promise.reject(new Error('Missing teeltPlanId or registratieJaar')),
    enabled: false,
  });

export const useGetCompaniesReport = (year?: number, enabled: boolean = false, rekenmotorVersie?: string): UseQueryResult<Blob | undefined, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'companies', year, rekenmotorVersie],
    retry: false,
    queryFn: () =>
      year
        ? fetchAuthenticatedForBlob<Blob>(`${BASE_URL}/rapportages/bedrijven/${year}${createRekenmotorVersieQuery(rekenmotorVersie)}`, {}, 120_000)
        : Promise.reject(new Error('Missing year')),
    enabled: enabled,
  });

export const useGetBevoegdGezagsByTypeId = (id?: number, enabled: boolean = false): UseQueryResult<BevoegdGezagOutput[], Error> => {
  return useQuery<BevoegdGezagOutput[], Error>({
    queryKey: [...QUERY_KEY, 'bevoegdGezagTypeId', id],
    queryFn: () =>
      id ? fetchAuthenticatedForJson<BevoegdGezagOutput[]>(`${BASE_URL}/bevoegdGezag/typeId/${id}`) : Promise.reject(new Error('Missing type id')),
    enabled: enabled,
  });
};

export const useGetMilieudiensten = (enabled: boolean = false): UseQueryResult<MilieudienstOutput[], Error> =>
  useQuery<MilieudienstOutput[], Error>({
    queryKey: [...QUERY_KEY, 'milieudiensten'],
    queryFn: () => fetchAuthenticatedForJson<MilieudienstOutput[]>(`${BASE_URL}/milieudienst/all`),
    enabled,
  });

export const useGetAggregateGewasReport = (year?: number, enabled: boolean = false, rekenmotorVersie?: string): UseQueryResult<Blob | undefined, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'aggregateGewas', year, rekenmotorVersie],
    retry: false,
    queryFn: () =>
      year
        ? fetchAuthenticatedForBlob<Blob>(`${BASE_URL}/rapportages/aggregatie-gewas/${year}${createRekenmotorVersieQuery(rekenmotorVersie)}`, {}, 120_000)
        : Promise.reject(new Error('Missing year')),
    enabled: enabled,
  });

export const useGetAggregateOtherReport = (input?: AggregatieRapportInput, enabled: boolean = false): UseQueryResult<Blob | undefined, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'aggregateOther', input],
    retry: false,
    queryFn: () =>
      input
        ? fetchAuthenticatedForBlob<Blob>(`${BASE_URL}/rapportages/aggregatie?${createAggregatieRapportParams(input)}`, {}, 120_000)
        : Promise.reject(new Error('Missing input')),
    enabled: enabled,
  });

export const usePostAggregateLandelijkReport = (input?: AggregatieLandelijkRapportInput, enabled: boolean = false): UseQueryResult<Blob | undefined, Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'aggregateLandelijk', input],
    retry: false,
    queryFn: () =>
      input
        ? fetchAuthenticatedForBlob<Blob>(`${BASE_URL}/rapportages/aggregatie/landelijk`, {
            json: input,
            method: 'POST',
          })
        : Promise.reject(new Error('Missing input')),
    enabled: enabled,
  });

export const useReviewEnvironmentReport = (): UseMutationResult<Review, Error, Review> => {
  const queryClient = new QueryClient();

  return useMutation<Review, Error, Review>({
    mutationFn: (review: Review) =>
      fetchAuthenticatedForJson<Review>(`${BASE_URL}/rapportages/milieu`, {
        json: review,
        method: 'PATCH',
      }),
    mutationKey: [...QUERY_KEY],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
    },
  });
};

const createRekenmotorVersieQuery = (rekenmotorVersie?: string): string => {
  if (!rekenmotorVersie) {
    return '';
  }

  const params = new URLSearchParams({ rekenmotorVersie });
  return `?${params.toString()}`;
};

const createAggregatieRapportParams = (input: AggregatieRapportInput): URLSearchParams => {
  const params = new URLSearchParams({
    registratiejaar: input.registratieJaar?.toString() ?? '',
    bevoegdGezagId: input.bevoegdGezagId?.toString() ?? '',
    bevoegdGezagTypeCode: input.bevoegdGezagTypeCode ?? '',
  });

  if (input.rekenmotorVersie) {
    params.set('rekenmotorVersie', input.rekenmotorVersie);
  }

  return params;
};
